import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { PhaseBanner, BackLink } from 'govuk-react-jsx';
import queryString from 'query-string';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import Template from './components/Template';
import CookiesBanner from './components/CookiesBanner';
import { saveLanguageCookie } from './utils/helpers';
import RoutesTreeMain from './routes/RoutesTreeMain';
import RoutesTreeDashboard from './routes/RoutesTreeDashboard';
import RoutesTreeLandlordReg from './routes/RoutesTreeLandlordReg';
import RoutesTreeLandlordApp from './routes/RoutesTreeLandlordApp';
import RoutesTreeInstaller from './routes/RoutesTreeInstaller';
import RoutesTreePmcReg from './routes/RoutesTreePmcReg';
import {
  setCurrentLanguage,
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
  getDashboardLink,
} from './features/app/appSlice';
import { AuthProvider } from './AuthContext';
import { auth } from './firebase';
import useDisplayMenu from './hooks/useDisplayMenu';
import useAuthChanged from './hooks/useAuthChanged';
import RoutesTreePmcClientReg from './routes/RoutesTreePmcClientReg';
import RoutesTreePmcApp from './routes/RoutesTreePmcApp';

// TODO: ensure there are no debugger statements in the code. no-debugger was changed to eslint warning only

// TODO: ! set DEBUG = false for PROD // toogle DEBUG flag enables/disables console.log function
let DEBUG = true;
if (process.env.NODE_ENV === 'development') {
  DEBUG = true;
}

if (!DEBUG) {
  // eslint-disable-next-line no-console
  console.log = () => {};
}

if (!('fetch' in window)) {
  window.fetch = fetch;
}

function App() {
  const gtmParams = { id: process.env.REACT_APP_GA_GTM };
  // these are required for the component to update on load of each screen
  const { search } = useLocation();
  queryString.parse(search);

  const dispatch = useDispatch();
  const dashboardLink = useSelector(getDashboardLink);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);

  const [currentUser, setCurrentUser] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loggedInAccountType, setLoggedInAccountType] = useState(null);

  const history = useHistory();

  useDisplayMenu();
  useAuthChanged(setCurrentUser, setLoggedInAccountType);

  // Copyprovider
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  const handleSignout = () => {
    signOut(auth)
      .then(() => {
        window.location.assign('/signin');
      })
      .catch(() => {
        // TODO: Show error message
      });
  };

  const headerProps =
    !currentUser || !currentUser.emailVerified || !dashboardLink
      ? {
          serviceName: copyProvider.getCopy(
            'App.s1',
            'OZEV Electric Vehicle Chargepoint Grants'
          ),
          serviceUrlHref: '/',
          navigation: [],
        }
      : {
          serviceName: copyProvider.getCopy(
            'App.s1',
            'OZEV Electric Vehicle Chargepoint Grants'
          ),
          serviceUrlHref: dashboardLink,
          navigation: [
            {
              active: dashboardLink && true,
              children: (
                <Link
                  className="govuk-header__link"
                  to={{}}
                  onClick={() => window.location.assign(dashboardLink)}
                >
                  {copyProvider.getCopy('App.s2', 'Dashboard')}
                </Link>
              ),
            },
            {
              active:
                window.location.pathname === '/dashboardHub/your-account' &&
                true,
              children: (
                <Link
                  className="govuk-header__link"
                  to="/dashboardHub/your-account"
                  onClick={() =>
                    window.location.assign('/dashboardHub/your-account')
                  }
                >
                  {copyProvider.getCopy('App.s3', 'Your account')}
                </Link>
              ),
            },
            {
              children: (
                <Link
                  className="govuk-header__link"
                  onClick={handleSignout}
                  to={{}}
                >
                  {copyProvider.getCopy('App.s4', 'Sign out')}
                </Link>
              ),
            },
          ],
        };

  const handleBtnClick = (event) => {
    event.preventDefault();
    history.goBack();
  };

  const beforeContentProps = (
    <>
      <PhaseBanner tag={{ children: 'beta' }}>
        {copyProvider.getCopy('App.s5', 'This is a new service - ')}
        <a
          href="https://www.smartsurvey.co.uk/s/EV-grants/"
          target="_blank"
          rel="noopener noreferrer"
          className="govuk-link"
        >
          {copyProvider.getCopy('App.s6', 'your feedback ')}
        </a>
        {copyProvider.getCopy('App.s7', 'to help us to improve this service.')}
      </PhaseBanner>
      <BackLink onClick={handleBtnClick}>
        {copyProvider.getCopy('App.s8', 'Back')}
      </BackLink>
    </>
  );

  const footerProps = {
    meta: {
      children: [
        <Link className="govuk-footer__link app-footer-links" to="/cookies">
          {copyProvider.getCopy('App.s9', 'Cookies')}
        </Link>,
        <Link
          className="govuk-footer__link app-footer-links"
          to="/privacy-policy"
          target="_blank"
        >
          {copyProvider.getCopy('App.s10', 'Privacy')}
        </Link>,
        <Link
          className="govuk-footer__link app-footer-links"
          to="/accessibility-statement"
          target="_blank"
        >
          {copyProvider.getCopy('App.s11', 'Accessibility statement')}
        </Link>,
        <Link
          className="govuk-footer__link app-footer-links"
          to="/terms-and-conditions"
          target="_blank"
        >
          {copyProvider.getCopy('App.s12', 'Terms and conditions')}
        </Link>,
        <Link
          className="govuk-footer__link app-footer-links"
          lang={currentLanguage === 'en' ? 'cy' : 'en'}
          onClick={() => {
            const selectedLanguage = currentLanguage === 'en' ? 'cy' : 'en';
            saveLanguageCookie(selectedLanguage);
            dispatch(setCurrentLanguage(selectedLanguage));
          }}
          to={{}}
        >
          {copyProvider.getCopy('App.s13', 'Cymraeg')}
        </Link>,
      ],
    },
  };

  let currentClassName;
  switch (window.location.pathname) {
    case '/signin':
    case '/installer/chargepoints-summary':
    case '/dashboardHub/dashboard':
      currentClassName = 'govuk-grid-column-full-width';
      break;
    case '/dashboardHub/your-account':
      currentClassName = 'govuk-width-container';
      break;
    default:
      currentClassName = 'govuk-grid-column-two-thirds';
  }

  return (
    <GTMProvider state={gtmParams}>
      <AuthProvider value={{ currentUser }}>
        <CookiesBanner />
        <Template
          header={headerProps}
          beforeContent={beforeContentProps}
          footer={footerProps}
        >
          <div className="govuk-grid-row">
            <div className={currentClassName}>
              <RoutesTreeMain />
              <RoutesTreeDashboard />
              <RoutesTreeLandlordReg />
              <RoutesTreeLandlordApp />
              <RoutesTreeInstaller />
              <RoutesTreePmcReg />
              <RoutesTreePmcClientReg />
              <RoutesTreePmcApp />
            </div>
          </div>
        </Template>
      </AuthProvider>
    </GTMProvider>
  );
}

export default App;
